"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type ShuntingRow = {
  id: string
  stationId: string
  slotId: string
  status: string
  providerId: string | null
  requestType: string
  fromTrack: number | null
  toTrack: number | null
  scheduledAt: string | null
  startedAt: string | null
  completedAt: string | null
}

type ShuntingResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

const STATUS_CLASS: Record<string, string> = {
  pending: 'text-yellow-600',
  accepted: 'text-blue-600',
  in_progress: 'text-orange-600',
  completed: 'text-green-600',
  cancelled: 'text-red-600',
}

const REQUEST_TYPE_KEYS: Record<string, string> = {
  arrival_shunting: 'shunting.type.arrival',
  departure_shunting: 'shunting.type.departure',
  repositioning: 'shunting.type.repositioning',
  coupling: 'shunting.type.coupling',
  decoupling: 'shunting.type.decoupling',
}

function mapRow(item: Record<string, unknown>): ShuntingRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    stationId: typeof item.station_id === 'string' ? item.station_id : '',
    slotId: typeof item.slot_id === 'string' ? item.slot_id : '',
    status: typeof item.status === 'string' ? item.status : 'pending',
    providerId: typeof item.provider_id === 'string' ? item.provider_id : null,
    requestType: typeof item.request_type === 'string' ? item.request_type : '',
    fromTrack: typeof item.from_track === 'number' ? item.from_track : null,
    toTrack: typeof item.to_track === 'number' ? item.to_track : null,
    scheduledAt: typeof item.scheduled_at === 'string' ? item.scheduled_at : null,
    startedAt: typeof item.started_at === 'string' ? item.started_at : null,
    completedAt: typeof item.completed_at === 'string' ? item.completed_at : null,
  }
}

export default function ShuntingRequestsPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'shunting-requests-list',
  })
  const [rows, setRows] = React.useState<ShuntingRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const getStatusLabel = React.useCallback((status: string) => {
    const fallbackMap: Record<string, string> = {
      pending: 'Pending',
      accepted: 'Accepted',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    }
    return t(`shunting.status.${status}`, fallbackMap[status] ?? status)
  }, [t])

  const getRequestTypeLabel = React.useCallback((requestType: string) => {
    const key = REQUEST_TYPE_KEYS[requestType]
    const fallbackMap: Record<string, string> = {
      arrival_shunting: 'Arrival Shunting',
      departure_shunting: 'Departure Shunting',
      repositioning: 'Repositioning',
      coupling: 'Coupling',
      decoupling: 'Decoupling',
    }
    return key ? t(key, fallbackMap[requestType] ?? requestType) : requestType
  }, [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'status',
      label: t('shunting.filters.status', 'Status'),
      type: 'select' as const,
      options: [
        { value: 'pending', label: getStatusLabel('pending') },
        { value: 'accepted', label: getStatusLabel('accepted') },
        { value: 'in_progress', label: getStatusLabel('in_progress') },
        { value: 'completed', label: getStatusLabel('completed') },
        { value: 'cancelled', label: getStatusLabel('cancelled') },
      ],
    },
  ], [getStatusLabel, t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const status = filterValues.status
    if (typeof status === 'string' && status.trim()) params.set('status', status)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: ShuntingResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<ShuntingResponse>(
          `/api/shunting/shunting-requests?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          flash(t('shunting.error.load', 'Failed to load shunting requests'), 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapRow).filter((row): row is ShuntingRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch {
        if (!cancelled) flash(t('shunting.error.load', 'Failed to load shunting requests'), 'error')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    try {
      return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
      return iso
    }
  }

  const handleDelete = React.useCallback(async (row: ShuntingRow) => {
    if (!row.id) return
    const confirmed = await confirm({
      title: t('shunting.confirm.delete', 'Delete this shunting request?'),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/shunting/shunting-requests?id=${encodeURIComponent(row.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('shunting.error.delete', 'Failed to delete shunting request') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('shunting.flash.deleted', 'Shunting request deleted'), 'success')
    } catch (err) {
      const message = err instanceof Error ? err.message : t('shunting.error.delete', 'Failed to delete shunting request')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const columns = React.useMemo<ColumnDef<ShuntingRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'requestType',
        header: t('shunting.columns.requestType', 'Type'),
        cell: ({ row }) => getRequestTypeLabel(row.original.requestType),
      },
      {
        accessorKey: 'status',
        header: t('shunting.columns.status', 'Status'),
        cell: ({ row }) => {
          const cls = STATUS_CLASS[row.original.status] ?? ''
          return <span className={cls}>{getStatusLabel(row.original.status)}</span>
        },
      },
      {
        accessorKey: 'fromTrack',
        header: t('shunting.columns.fromTrack', 'From Track'),
        cell: ({ row }) => row.original.fromTrack !== null ? <span className="font-mono">{row.original.fromTrack}</span> : noValue,
      },
      {
        accessorKey: 'toTrack',
        header: t('shunting.columns.toTrack', 'To Track'),
        cell: ({ row }) => row.original.toTrack !== null ? <span className="font-mono">{row.original.toTrack}</span> : noValue,
      },
      {
        accessorKey: 'providerId',
        header: t('shunting.columns.provider', 'Provider'),
        cell: ({ row }) => row.original.providerId
          ? <span className="font-mono text-xs">{row.original.providerId.slice(0, 8)}</span>
          : noValue,
      },
      {
        accessorKey: 'scheduledAt',
        header: t('shunting.columns.scheduledAt', 'Scheduled'),
        cell: ({ row }) => formatDateTime(row.original.scheduledAt),
      },
      {
        accessorKey: 'completedAt',
        header: t('shunting.columns.completedAt', 'Completed'),
        cell: ({ row }) => formatDateTime(row.original.completedAt),
      },
    ]
  }, [getRequestTypeLabel, getStatusLabel, t])

  return (
    <Page>
      <PageBody>
        <DataTable<ShuntingRow>
          title={t('shunting.title', 'Shunting Requests')}
          refreshButton={{
            label: t('shunting.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); setReloadToken((n) => n + 1) },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/shunting-requests/new">
                {t('shunting.actions.create', 'New Request')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('shunting.searchPlaceholder', 'Search shunting requests...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(v) => { setFilterValues(v); setPage(1) }}
          onFiltersClear={() => { setFilterValues({}); setPage(1) }}
          perspective={{ tableId: 'shunting.shunting_requests.list' }}
          onRowClick={(row) => router.push(`/backend/shunting-requests/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('shunting.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
